import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useMergeLearners } from 'api';
import {
  type MergeLearnersRequest,
  type MergeReason,
} from 'api/organisations/organisationApitypes';
import { type MergeLearnersDialogProps } from './MergeLearnersDialog';
import { type MergeLearnersErrorDialogProps } from './MergeLearnersErrorDialog';
import useGetMergeLearnerTarget from './useGetMergeLearnerTarget';

export type UseMergeLearnersDialogResult = {
  offerMerge: (email: string) => void;
  reset: () => void;
  isBusy: boolean;
  lookupError: Error | null;
  dialogProps: MergeLearnersDialogProps | undefined;
  errorDialogProps: MergeLearnersErrorDialogProps | undefined;
};

const useMergeLearnersDialog = (
  organisationId: string,
  sourceLearnerId: string,
): UseMergeLearnersDialogResult => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    resolve,
    target,
    isResolving,
    error: lookupError,
    reset: resetTarget,
  } = useGetMergeLearnerTarget(organisationId);

  const {
    mutate,
    isPending: isMerging,
    reset: resetMerge,
  } = useMergeLearners(queryClient);

  const [failedRequest, setFailedRequest] = useState<MergeLearnersRequest>();

  const reset = () => {
    resetTarget();
    resetMerge();
    setFailedRequest(undefined);
  };

  const submitMerge = (request: MergeLearnersRequest) =>
    mutate(request, {
      onSuccess: () =>
        navigate(
          `/learners/${request.organisationId}/${request.targetLearnerId}`,
        ),
      onError: () => setFailedRequest(request),
    });

  const confirm = (reason: MergeReason, customReason?: string) => {
    if (!target) {
      return;
    }
    submitMerge({
      organisationId,
      sourceLearnerId,
      targetLearnerId: target.id,
      reason,
      customReason,
    });
  };

  const getDialogProps = () => {
    if (!target || failedRequest) {
      return undefined;
    }
    return {
      target,
      onClose: reset,
      onConfirm: confirm,
      isLoading: isMerging,
    };
  };

  const getErrorDialogProps = () => {
    if (!failedRequest) {
      return undefined;
    }
    return {
      onRetry: () => submitMerge(failedRequest),
      onCancel: reset,
      isLoading: isMerging,
    };
  };

  return {
    offerMerge: resolve,
    reset,
    isBusy: isResolving || isMerging,
    lookupError,
    dialogProps: getDialogProps(),
    errorDialogProps: getErrorDialogProps(),
  };
};

export default useMergeLearnersDialog;
